/**
 * Event bus which forwards server-side organization events.
 *
 * This is a singleton so every import gets the same instance.
 */
export const ORGANIZATION_EVENTS_RECONNECTED_EVENT =
    'organization-events-reconnected';
export const ORGANIZATION_EVENTS_CONNECTION_OPENED_EVENT =
    'organization-events-connection-opened';
export const ORGANIZATION_EVENTS_CONNECTION_CLOSED_EVENT =
    'organization-events-connection-closed';
// Fired when a delivered event's org_seq shows a gap: at least one
// organization event was published but never reached this client.
export const ORGANIZATION_EVENTS_GAP_DETECTED_EVENT =
    'organization-events-gap-detected';

export type OrganizationEventsReconnectedDetail = {
    readonly organizationId: string;
};

export type OrganizationEventsGapDetectedDetail = {
    readonly organizationId: string;
};

export type OrganizationEventsConnectionDetail = {
    readonly organizationId: string;
};

export type OrganizationEventBusDetail<T> = {
    readonly organizationId: string | null;
    readonly event: T;
};

export const organizationEventBus = new EventTarget();

// Fired whenever the session provider confirms a live session against Ory,
// including the revalidation that follows a recovered credential. Kept off
// `organizationEventBus` because a session is not scoped to an organization,
// and folding it in would deliver session traffic to every organization-event
// consumer.
export const SESSION_VALIDATED_EVENT = 'session-validated';
export const SESSION_ENDED_EVENT = 'session-ended';

export type SessionEndedDetail = Readonly<{
    readonly reason: 'session_loss' | 'logout';
}>;

export type SessionEndedReason = SessionEndedDetail['reason'];

function sessionEndedReason(event: Event): SessionEndedReason | null {
    if (!(event instanceof CustomEvent)) {
        return null;
    }
    const detail: unknown = event.detail;
    if (
        typeof detail !== 'object' ||
        detail === null ||
        !('reason' in detail) ||
        (detail.reason !== 'session_loss' && detail.reason !== 'logout')
    ) {
        return null;
    }
    return detail.reason;
}

// Validate at the bus boundary so no eager or independently registered purge
// listener can observe a malformed session-ended event.
class SessionEventTarget extends EventTarget {
    public override dispatchEvent(event: Event): boolean {
        if (
            event.type === SESSION_ENDED_EVENT &&
            sessionEndedReason(event) === null
        ) {
            throw new Error('Session-ended event detail is invalid.');
        }
        return super.dispatchEvent(event);
    }
}

export const sessionEventBus = new SessionEventTarget();

// The eager purge must know this key without importing the chat feature,
// which is barred from this module. Exporting it keeps ONE source of truth:
// a duplicated literal could drift, and the purge would then silently stop
// clearing a departing person's queued text with no test noticing.
export const MESSAGE_OUTBOX_STORAGE_KEY =
    'phoebe:v3-agent-chat:message-outbox:v1';

// sessionStorage is deliberately per-tab. A session end can purge this tab's
// queued text synchronously, but browsers do not expose another tab's storage.
sessionEventBus.addEventListener(SESSION_ENDED_EVENT, (event) => {
    if (sessionEndedReason(event) === null) {
        return;
    }
    try {
        window.sessionStorage.removeItem(MESSAGE_OUTBOX_STORAGE_KEY);
    } catch {
        // sessionStorage can be unavailable in partitioned or private frames.
    }
});

export function dispatchSessionEnded(reason: SessionEndedReason): void {
    sessionEventBus.dispatchEvent(
        new CustomEvent<SessionEndedDetail>(SESSION_ENDED_EVENT, {
            detail: { reason },
        }),
    );
}

export function subscribeSessionEnded(
    listener: (detail: SessionEndedDetail) => void,
): () => void {
    const eventListener = (event: Event) => {
        const reason = sessionEndedReason(event);
        if (!(event instanceof CustomEvent)) {
            throw new Error(
                'Session-ended events must be CustomEvent objects.',
            );
        }
        if (reason === null) {
            throw new Error('Session-ended event detail is invalid.');
        }
        listener({ reason });
    };
    sessionEventBus.addEventListener(SESSION_ENDED_EVENT, eventListener);
    return () => {
        sessionEventBus.removeEventListener(SESSION_ENDED_EVENT, eventListener);
    };
}

export function isOrganizationEventTestEnvironment(
    hostname: string,
    isDevelopmentEnvironment: boolean,
): boolean {
    if (!isDevelopmentEnvironment) {
        return false;
    }

    // Private Daytona local-app tests use this HTTPS hostname so auth and
    // secure-cookie behavior match a deployed app without exposing test seams
    // in staging or production builds.
    return (
        ['localhost', '127.0.0.1'].includes(hostname) ||
        hostname === 'phoebe.daytona.internal'
    );
}
